import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Accessibility, ArrowLeft, BarChart3, BookOpenCheck, Feather,
  Handshake, HeartHandshake, Leaf, Recycle, Scale, Sprout, UsersRound,
} from 'lucide-react';
import { FONTS, GRAIN_URL, P, useScriptoriumFonts } from '@/components/landing/scriptorium/palette';
import { CommunityPublishingFundSection } from '@/components/community/CommunityPublishingFundSection';
import { useSeoMeta } from '@/lib/useSeoMeta';
import { useHreflang } from '@/lib/useHreflang';
import PublicNavBar from '@/components/landing/PublicNavBar';
import StructuredData, { breadcrumbLd, webPageLd } from '@/components/seo/StructuredData';

const SEO_FALLBACK = {
  title:       'Diversity, Equity & Sustainability — Crown & Rose',
  description: 'Crown & Rose\u2019s public DEI and sustainability charter: commitments to underrepresented communities, people with disabilities, the LGBTQIA+ community, and responsible, lower-impact publishing.',
  keywords:    'crown and rose diversity equity inclusion, indie publishing sustainability charter, DEI commitment statement',
};

const communities = [
  {
    id: 'minorities', icon: UsersRound, title: 'Underrepresented Racial and Ethnic Groups in Business',
    body: 'We recognize that, despite decades of stated goals, systemic barriers continue to exclude Black, Latino, Asian, and other underrepresented professionals from C-suite and executive board roles. Crown & Rose commits to intentional recruitment, advancement, and retention of racially and ethnically diverse talent at every level—especially in leadership, where representation remains most scarce. We strive to set measurable goals, track progress transparently, and hold ourselves accountable.',
  },
  {
    id: 'disabilities', icon: Accessibility, title: 'People with Disabilities',
    body: 'Visible and hidden impairments are too often met with environments that were never designed for them. Accessibility is not a courtesy; it is a right. Crown & Rose commits to accessible design in our workplaces, products, and services; to proper workplace adjustments as standard practice; and to a culture of disability awareness and inclusion that recognizes the full spectrum of human ability.',
  },
  {
    id: 'lgbtqia', icon: HeartHandshake, title: 'The LGBTQIA+ Community',
    body: 'We know that many employees seek safer, more inclusive environments where sexual orientation and gender identity can be expressed freely and without fear. Crown & Rose commits to being that environment through inclusive policies, benefits that support all family structures, anti-discrimination protections, and a workplace culture where every person can bring their whole self to work.',
  },
  {
    id: 'women-nonbinary', icon: Scale, title: 'Women and Non-Binary Employees',
    body: 'While representation at entry levels is strong, leadership pipelines continue to narrow sharply at senior management. Crown & Rose commits to closing that gap through intentional sponsorship, equitable advancement pathways, pay transparency, and leadership accountability—so that ambition is met with opportunity at every stage of a career.',
  },
];

const sustainability = [
  { id: 'sourcing', icon: Sprout, title: 'Responsible sourcing', body: 'We will prioritize responsibly sourced paper, packaging, and production materials, and consider the environmental practices of the partners from whom we buy.' },
  { id: 'pod', icon: BookOpenCheck, title: 'Lower-impact publishing', body: 'We will favor print-on-demand and efficient digital workflows that reduce unsold inventory, unnecessary transport, and the resource burden of overproduction.' },
  { id: 'waste', icon: Recycle, title: 'Reduced waste', body: 'We will use digital proofing, right-sized and recyclable packaging, and practical alternatives to single-use materials wherever our operations allow.' },
  { id: 'reporting', icon: BarChart3, title: 'Measurable reporting', body: 'We will establish environmental goals, measure progress, and report what we learn—including where our performance still needs to improve.' },
  { id: 'suppliers', icon: Handshake, title: 'Supplier accountability', body: 'We will ask suppliers and production partners to document their environmental practices, meet clear expectations, and participate in continuous improvement.' },
];

const accountability = [
  ['Measurable goals', 'Public progress reporting across hiring, retention, supplier diversity, and leadership representation.'],
  ['Accountability at the top', 'Executive ownership of DEI and sustainability outcomes, supported by board-level oversight.'],
  ['Continuous learning', 'Equipping our teams to build inclusive environments—not merely diverse ones.'],
  ['Community investment', 'Directing commerce and resources toward minority-owned and marginalized businesses to advance shared prosperity.'],
];

export default function DiversitySustainabilityPage() {
  useScriptoriumFonts();
  useSeoMeta('/diversity-sustainability', SEO_FALLBACK);
  useHreflang('diversity-sustainability');
  const navigate  = useNavigate();
  const siteUrl   = typeof window !== 'undefined' ? window.location.origin : 'https://crownnrose.com';
  const crumbs    = breadcrumbLd([
    { name: 'Home',  item: `${siteUrl}/` },
    { name: 'Diversity & Sustainability', item: `${siteUrl}/diversity-sustainability` },
  ]);
  const wpLd      = webPageLd({
    siteUrl,
    path:        '/diversity-sustainability',
    name:        'Diversity & Sustainability — Crown & Rose',
    description: SEO_FALLBACK.description,
  });

  return (
    <main
      className="h-full overflow-y-auto selection:bg-[#C8A96E]/50"
      style={{ backgroundColor: P.paper, color: P.ink, fontFamily: FONTS.body }}
      data-testid="diversity-sustainability-page"
    >
      <StructuredData id="dei-breadcrumb" data={crumbs} />
      <StructuredData id="dei-page"       data={wpLd}   />
      <style>{`
        @keyframes charter-rise { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        .charter-rise { opacity: 0; animation: charter-rise .7s ease-out forwards; }
      `}</style>
      <div className="pointer-events-none fixed inset-0 opacity-25" style={{ backgroundImage: GRAIN_URL }} />

      <PublicNavBar
        onSignIn={() => navigate('/login')}
        onSignUp={() => navigate('/login?mode=signup')}
      />

      <section className="relative z-10 border-b" style={{ borderColor: `${P.gold}66` }} data-testid="charter-header">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 sm:px-8 lg:grid-cols-[160px_1fr] lg:py-20">
          <div className="charter-rise flex items-start justify-center lg:justify-start">
            <div className="flex h-32 w-32 items-center justify-center rounded-full border-2 p-5 shadow-lg" style={{ borderColor: P.gold, backgroundColor: P.oxblood }}>
              <img src="/brand/crownandrose-logo.png" alt="Crown & Rose emblem" className="h-full w-full object-contain" data-testid="charter-brand-emblem" />
            </div>
          </div>
          <div className="charter-rise" style={{ animationDelay: '120ms' }}>
            <div className="text-[11px] uppercase tracking-[0.3em]" style={{ color: P.oxblood, fontFamily: FONTS.type }}>Diversity · Equity · Inclusion · Stewardship</div>
            <h1 className="mt-4 max-w-4xl text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl" style={{ fontFamily: FONTS.display }} data-testid="charter-header-title">
              Diversity &amp; Sustainability
            </h1>
            <p className="mt-3 text-base font-semibold sm:text-lg" style={{ color: P.leather, fontFamily: FONTS.display }} data-testid="dei-statement-title">
              Crown &amp; Rose — Diversity, Equity &amp; Inclusion Statement
            </p>
            <p className="mt-6 max-w-3xl text-sm leading-7 sm:text-base" style={{ color: P.inkSoft }}>
              At Crown &amp; Rose, diversity, equity, and inclusion are not aspirations we merely endorse—they are obligations we embrace. We believe commerce is one of the most powerful engines for community development, and we are committed to wielding that power responsibly by creating real opportunities for minorities and marginalized groups to build, own, and grow within the communities they call home.
            </p>
            <div className="mt-7 inline-flex items-center gap-2 border-y px-1 py-2 text-sm italic sm:text-base" style={{ borderColor: `${P.gold}99`, color: P.leather, fontFamily: FONTS.display }} data-testid="charter-motto">
              <Feather className="h-4 w-4" /> Commerce that builds community. Inclusion that endures.
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10" data-testid="dei-commitment-section">
        <div className="mx-auto max-w-4xl px-5 py-14 sm:px-8 lg:py-20">
          <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: P.oxblood, fontFamily: FONTS.type }}>Our commitment</div>
          <h2 className="mt-3 text-base font-semibold md:text-lg" style={{ fontFamily: FONTS.display }}>Commerce as a tool for community development</h2>
          <p className="mt-5 text-sm leading-7 sm:text-base" style={{ color: P.inkSoft }}>
            <span className="float-left pr-3 pt-1 text-5xl font-semibold leading-[0.8] sm:text-6xl" style={{ color: P.oxblood, fontFamily: FONTS.display }}>C</span>
            Crown &amp; Rose pledges to direct our purchasing power, partnerships, and platform toward businesses and entrepreneurs from underserved communities. We will actively create commerce opportunities—through supplier partnerships, mentorship, access to capital, and market visibility—that fuel local economic development in minority and marginalized neighborhoods. We measure success not only by what we earn, but by what we help build.
          </p>
        </div>
      </section>

      <CommunityPublishingFundSection />

      <section className="relative z-10 border-y" style={{ borderColor: `${P.gold}66`, backgroundColor: P.paperDeep }}>
        <div className="mx-auto max-w-6xl px-5 py-14 sm:px-8 lg:py-20">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: P.oxblood, fontFamily: FONTS.type }}>Representation with responsibility</div>
            <h2 className="mt-3 text-base font-semibold md:text-lg" style={{ fontFamily: FONTS.display }}>Who we stand with</h2>
          </div>
          <div className="mt-8 grid gap-4 md:grid-cols-2" data-testid="who-we-stand-with-grid">
            {communities.map(({ id, icon: Icon, title, body }, index) => (
              <article key={id} className="charter-rise rounded-lg border p-6 shadow-sm" style={{ animationDelay: `${index * 90}ms`, borderColor: `${P.gold}88`, backgroundColor: P.paper }} data-testid={`stand-with-${id}-card`}>
                <Icon className="h-6 w-6" style={{ color: P.oxblood }} aria-hidden="true" />
                <h3 className="mt-4 text-lg font-semibold" style={{ fontFamily: FONTS.display }}>{title}</h3>
                <p className="mt-3 text-sm leading-6 sm:text-base" style={{ color: P.inkSoft }}>{body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10" data-testid="sustainability-section">
        <div className="mx-auto max-w-6xl px-5 py-14 sm:px-8 lg:py-20">
          <div className="grid gap-8 lg:grid-cols-[0.7fr_1.3fr]">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.28em]" style={{ color: P.verdant, fontFamily: FONTS.type }}><Leaf className="h-4 w-4" /> Environmental stewardship</div>
              <h2 className="mt-3 text-base font-semibold md:text-lg" style={{ fontFamily: FONTS.display }}>Publishing with tomorrow in mind</h2>
              <p className="mt-5 text-sm leading-7 sm:text-base" style={{ color: P.inkSoft }}>
                Sustainability is inseparable from the future of independent publishing. Crown &amp; Rose commits to an operation that respects finite resources, favors lower-impact production, reduces avoidable waste, and holds suppliers to clear environmental expectations. We will measure our progress honestly and improve as better methods become available.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {sustainability.map(({ id, icon: Icon, title, body }, index) => (
                <article key={id} className={`rounded-lg border-l-4 p-5 ${index === 4 ? 'sm:col-span-2' : ''}`} style={{ borderColor: P.verdant, backgroundColor: P.paperDeep }} data-testid={`sustainability-pillar-${id}`}>
                  <div className="flex items-center gap-2"><Icon className="h-5 w-5" style={{ color: P.verdant }} /><h3 className="text-base font-semibold" style={{ fontFamily: FONTS.display }}>{title}</h3></div>
                  <p className="mt-2 text-sm leading-6" style={{ color: P.inkSoft }}>{body}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 border-y" style={{ borderColor: `${P.gold}66`, backgroundColor: P.paperDeep }} data-testid="accountability-section">
        <div className="mx-auto max-w-4xl px-5 py-14 sm:px-8 lg:py-20">
          <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: P.oxblood, fontFamily: FONTS.type }}>How we hold ourselves accountable</div>
          <div className="mt-7 divide-y" style={{ borderColor: `${P.gold}66` }}>
            {accountability.map(([title, body], index) => (
              <div key={title} className="grid gap-2 py-5 sm:grid-cols-[42px_1fr]" style={{ borderColor: `${P.gold}66` }} data-testid={`accountability-item-${index + 1}`}>
                <span className="font-mono text-xs" style={{ color: P.oxblood }}>0{index + 1}</span>
                <div><h3 className="text-base font-semibold" style={{ fontFamily: FONTS.display }}>{title}</h3><p className="mt-1 text-sm leading-6" style={{ color: P.inkSoft }}>{body}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative z-10 mx-auto max-w-4xl px-5 py-14 sm:px-8 lg:py-20" data-testid="charter-promise-section">
        <div className="border-l-4 pl-6 sm:pl-9" style={{ borderColor: P.oxblood }}>
          <h2 className="text-base font-semibold md:text-lg" style={{ fontFamily: FONTS.display }}>Our promise</h2>
          <p className="mt-5 text-sm leading-7 sm:text-base" style={{ color: P.inkSoft }}>
            Crown &amp; Rose will not treat diversity as a statement to publish, but as a standard to practice. We will support minorities and marginalized groups not with words alone, but with commerce, opportunity, and sustained investment in community development. We will listen, we will measure, and we will act—because true inclusion is built, not declared.
          </p>
          <div className="mt-9 flex items-center gap-4">
            <img src="/brand/crownandrose-logo.png" alt="" aria-hidden="true" className="h-14 w-14 object-contain" />
            <div><div className="text-2xl font-semibold" style={{ fontFamily: FONTS.display }}>Crown &amp; Rose</div><div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: P.inkSoft, fontFamily: FONTS.type }}>Commerce that builds community</div></div>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t" style={{ borderColor: `${P.gold}77`, backgroundColor: P.paperDeep }}>
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-5 py-6 text-xs sm:px-8" style={{ color: P.inkSoft }}>
          <span>© 2026 Crown &amp; Rose LLC</span>
          <Link to="/" className="inline-flex items-center gap-2 font-semibold transition-opacity hover:opacity-70" style={{ color: P.oxblood }} data-testid="diversity-sustainability-footer-home-link"><ArrowLeft className="h-3.5 w-3.5" /> Return to the Scriptorium</Link>
        </div>
      </footer>
    </main>
  );
}